import { useState } from 'react';
import { requestAiDraft, saveBrief, type AiDraft, type QualityBrief } from '../../lib/api.js';
import { t } from '../../lib/i18n.js';

/**
 * The Quality Assessment Brief, with its AI panel (spec §9, ADR-0008).
 *
 * Three explicit buttons — Accept, Edit, Reject — and **never an implicit
 * accept**. A draft that silently becomes the narrative because the user clicked
 * away is exactly what ADR-0008 forbids, and the server rejects that claim anyway.
 *
 * A timeout is presented as a normal outcome: the message is informational, the
 * textarea stays usable, and nothing is blocked. The user carries on writing.
 */
export function QualityBriefPanel({
  periodId,
  brief,
  readOnly,
  onSaved,
}: {
  periodId: string;
  brief: QualityBrief | null;
  readOnly: boolean;
  onSaved: () => void;
}) {
  const [narrative, setNarrative] = useState(brief?.narrative ?? '');
  const [draft, setDraft] = useState<AiDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const requestDraft = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await requestAiDraft(periodId);
      setDraft(result);
      if (result.unavailableReason) setMessage(result.unavailableReason);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("Could not request a draft."));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Save with an explicit disposition.
   *
   * The disposition sent here is a CLAIM. The server verifies it against the
   * suggestion's text and records what actually happened — so clicking Accept
   * after editing is recorded as an edit, not as an acceptance.
   */
  const save = async (disposition: 'accepted' | 'edited' | 'rejected' | null, text: string) => {
    setBusy(true);
    setError(null);
    try {
      await saveBrief(periodId, {
        narrative: text,
        // Only a draft still waiting is disposed of here. Once recorded, the
        // brief's AI link and outcome stay as they are on every later save
        // (DATA-14) — the server keeps them.
        aiSuggestionId: disposition && draft?.suggestionId ? draft.suggestionId : null,
        aiDisposition: disposition && draft?.suggestionId ? disposition : null,
      });
      setNarrative(text);
      setDraft(null);
      setMessage(t("Brief saved."));
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("Could not save the brief."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-md border border-border bg-surface p-5 shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="m-0 text-base font-semibold">{t("Quality Assessment Brief")}</h2>
        {!readOnly && (
          <button
            type="button"
            onClick={() => void requestDraft()}
            disabled={busy}
            className="rounded-sm border border-ag-purple px-3 py-1.5 text-sm font-medium text-ag-purple disabled:opacity-60"
          >
            {busy ? t("Working…") : t("Draft with AI")}
          </button>
        )}
      </div>

      {message && (
        <p role="status" className="mt-3 rounded-sm bg-tone-info-surface px-3 py-2 text-sm text-content-secondary">
          {message}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-3 rounded-sm bg-tone-critical-surface px-3 py-2 text-sm text-tone-critical">
          {error}
        </p>
      )}

      {draft?.text && (
        <div className="mt-4 rounded-sm border border-border bg-surface-sunken p-4">
          <p className="m-0 text-xs font-semibold uppercase tracking-wide text-content-secondary">
            {t("AI draft — review before using")}
          </p>
          <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-content-primary">
            {draft.text}
          </pre>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void save('accepted', draft.text!)}
              className="rounded-sm bg-ag-purple px-3 py-1.5 text-sm font-medium text-content-inverse disabled:opacity-60"
            >
              {t("Accept")}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                // Put the draft in the editor and let the user change it. Saving
                // afterwards records an edit, because that is what it is.
                setNarrative(draft.text!);
                setMessage(t("Draft moved into the editor. Change it, then save."));
              }}
              className="rounded-sm border border-border-strong px-3 py-1.5 text-sm font-medium"
            >
              {t("Edit")}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void save('rejected', narrative)}
              className="rounded-sm border border-border-strong px-3 py-1.5 text-sm font-medium"
            >
              {t("Reject")}
            </button>
          </div>
        </div>
      )}

      <div className="mt-4">
        <label htmlFor="brief-narrative" className="text-sm font-medium">
          {t("Monthly narrative")}
        </label>
        <textarea
          id="brief-narrative"
          rows={10}
          readOnly={readOnly}
          value={narrative}
          onChange={(event) => setNarrative(event.target.value)}
          className="mt-1.5 w-full rounded-sm border border-border-strong px-3 py-2 text-sm text-content-primary read-only:bg-surface-sunken"
        />
      </div>

      {brief?.aiDisposition && (
        <p className="mt-2 text-xs text-content-secondary">
          {t("AI assistance: the draft was")}{' '}<strong>{brief.aiDisposition}</strong>{' '}{t("by the author.")}
        </p>
      )}

      {!readOnly && (
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            void save(
              // Saving after moving a waiting AI draft into the editor records
              // an edit. With no draft waiting, this is a plain save.
              draft?.suggestionId ? 'edited' : null,
              narrative,
            )
          }
          className="mt-3 rounded-sm bg-ag-purple px-4 py-2 text-sm font-medium text-content-inverse disabled:opacity-60"
        >
          {t("Save brief")}
        </button>
      )}
    </section>
  );
}
